import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../../components/ui/Card';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Instagram, MessageCircle, FileSpreadsheet, Search, Loader2, Play, RefreshCw,
  Heart, CornerDownRight, AlertCircle, ChevronDown, ChevronUp, StopCircle,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/instagram-radar/comments';

const fmtNum = (v) => Number(v || 0).toLocaleString('en-US');
const fmtDate = (sec) => {
  if (!sec) return '';
  try { return new Date(sec * 1000).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

export default function InstagramComments() {
  const [username, setUsername] = useState('');
  const [maxPosts, setMaxPosts] = useState(30);
  const [maxPagesPerPost, setMaxPagesPerPost] = useState(30);
  const [showAdv, setShowAdv] = useState(false);

  const [activeUser, setActiveUser] = useState(''); // collection'ı başlatılan hesap
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [selectedPost, setSelectedPost] = useState('');
  const [sort, setSort] = useState('recent');
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fetchList = useCallback(async (user, post, srt) => {
    try {
      const qs = new URLSearchParams({ username: user, sort: srt, limit: '500' });
      if (post) qs.set('postCode', post);
      const res = await axios.get(`${API}/list?${qs}`, { withCredentials: true });
      setData(res.data);
    } catch (err) { /* sessiz */ }
  }, []);

  const pollStatus = useCallback(async (user) => {
    try {
      const res = await axios.get(`${API}/status?username=${encodeURIComponent(user)}`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.status === 'running' || res.data.status === 'stopping') {
        fetchList(user, '', sort); // ilerledikçe sonuçları tazele
      } else {
        clearInterval(pollRef.current);
        pollRef.current = null;
        fetchList(user, selectedPost, sort);
        if (res.data.status === 'done') toast.success('Toplama tamamlandı');
        if (res.data.status === 'stopped') toast.success('Toplama durduruldu — toplanan veri hazır');
        if (res.data.status === 'error') toast.error('Hata: ' + (res.data.error || ''));
      }
    } catch (err) { /* sessiz */ }
  }, [fetchList, sort, selectedPost]);

  const startCollect = async () => {
    const u = username.trim();
    if (!u) { toast.error('Instagram kullanıcı adı/URL girin'); return; }
    setStarting(true); setData(null); setStatus(null); setSelectedPost('');
    try {
      const res = await axios.post(`${API}/collect`, { username: u, maxPosts, maxPagesPerPost }, { withCredentials: true });
      const user = res.data.username || u;
      setActiveUser(user);
      if (pollRef.current) clearInterval(pollRef.current);
      pollStatus(user);
      pollRef.current = setInterval(() => pollStatus(user), 4000);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally { setStarting(false); }
  };

  const stopCollect = async () => {
    if (!activeUser) return;
    try {
      await axios.post(`${API}/stop`, { username: activeUser }, { withCredentials: true });
      toast('Durduruluyor…');
      setStatus((s) => (s ? { ...s, status: 'stopping' } : s));
    } catch (err) { toast.error(err?.response?.data?.error || err.message); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { if (activeUser) fetchList(activeUser, selectedPost, sort); }, [selectedPost, sort, activeUser, fetchList]);

  const isRunning = status?.status === 'running';
  const isBusy = isRunning || status?.status === 'stopping';
  const progressPct = status?.postsTotal ? Math.round((status.postsDone / status.postsTotal) * 100) : 0;
  const comments = (data?.comments || []).filter((c) =>
    !search || (c.text || '').toLowerCase().includes(search.toLowerCase()) || (c.user || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1200px] mx-auto">
      {/* Header / input */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-pink-50 border border-pink-100 shadow-sm rounded-xl">
            <Instagram className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Instagram Comments Collector</h1>
            <p className="text-sm text-gray-500">Bir hesabın postlarının altındaki tüm yorumları toplar (RapidAPI scraper).</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Instagram className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && startCollect()}
              placeholder="kullanıcı adı, @kullanıcı veya instagram.com/kullanıcı"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-100 outline-none"
            />
          </div>
          <button onClick={() => setShowAdv((v) => !v)}
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">
            Ayarlar {showAdv ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {isBusy ? (
            <button onClick={stopCollect} disabled={status?.status === 'stopping'}
              className="px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-all disabled:opacity-60 flex items-center gap-2">
              {status?.status === 'stopping' ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" />}
              {status?.status === 'stopping' ? 'Durduruluyor…' : 'Durdur'}
            </button>
          ) : (
            <button onClick={startCollect} disabled={starting}
              className="px-4 py-2.5 bg-pink-600 text-white rounded-xl text-sm font-medium hover:bg-pink-700 transition-all disabled:opacity-50 flex items-center gap-2">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Topla
            </button>
          )}
        </div>
        {showAdv && (
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
            <label className="flex items-center gap-2">Maks. post
              <input type="number" min={1} max={200} value={maxPosts} onChange={(e) => setMaxPosts(Number(e.target.value))}
                className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg" /></label>
            <label className="flex items-center gap-2">Post başına maks. sayfa (×15 yorum)
              <input type="number" min={1} max={200} value={maxPagesPerPost} onChange={(e) => setMaxPagesPerPost(Number(e.target.value))}
                className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg" /></label>
          </div>
        )}
      </div>

      {/* Progress */}
      {status && status.status !== 'none' && (
        <Card className="rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin text-pink-600" /> : <RefreshCw className="w-4 h-4 text-gray-400" />}
              @{status.username}
              <span className={`text-xs px-2 py-0.5 rounded-full ${status.status === 'done' ? 'bg-emerald-50 text-emerald-700' : status.status === 'error' ? 'bg-red-50 text-red-700' : 'bg-pink-50 text-pink-700'}`}>
                {status.status}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {fmtNum(status.postsDone)}/{fmtNum(status.postsTotal)} post · <b className="text-gray-800">{fmtNum(status.commentsCollected)}</b> yorum
            </div>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-pink-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          {status.error && <div className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {status.error}</div>}
        </Card>
      )}

      {/* Results */}
      {data && (
        <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-pink-500" />
              <span className="font-medium text-gray-800">{fmtNum(data.total)} yorum</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Yorumlarda ara…"
                  className="pl-8 pr-3 py-2 w-44 bg-white border border-gray-200 rounded-xl text-sm" />
              </div>
              <select value={selectedPost} onChange={(e) => setSelectedPost(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm max-w-[260px]">
                <option value="">Tüm postlar ({(data.posts || []).length})</option>
                {(data.posts || []).map((p) => (
                  <option key={p.postCode} value={p.postCode}>{(p.caption || p.postCode).slice(0, 40)} ({p.collected})</option>
                ))}
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm">
                <option value="recent">En yeni</option>
                <option value="likes">En çok beğeni</option>
              </select>
              <a href={`${API}/export?username=${encodeURIComponent(activeUser)}`}
                className="px-3 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5">
                <FileSpreadsheet className="w-4 h-4" /> Excel indir
              </a>
            </div>
          </div>
          <div className="overflow-auto max-h-[640px]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Kullanıcı</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Yorum</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Beğeni</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Yanıt</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {comments.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <a href={`https://www.instagram.com/${c.user}/`} target="_blank" rel="noreferrer" className="text-pink-600 hover:underline font-medium">@{c.user}</a>
                    </td>
                    <td className="px-4 py-2 text-gray-800 max-w-[560px]">{c.text}</td>
                    <td className="px-4 py-2 text-right text-gray-600 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-400" />{fmtNum(c.likeCount)}</span></td>
                    <td className="px-4 py-2 text-right text-gray-500 whitespace-nowrap">{c.childCount ? <span className="inline-flex items-center gap-1"><CornerDownRight className="w-3.5 h-3.5" />{c.childCount}</span> : '—'}</td>
                    <td className="px-4 py-2 text-right text-gray-400 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                  </tr>
                ))}
                {!comments.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">{isRunning ? 'Toplanıyor…' : 'Yorum yok'}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
